// sentItemsBatchClient — GET /api/sent-items/batch (sol finding 5).
//
// The household file opens with every in-motion row already expanded, and each
// expansion needs that item's blocks ①–③. It used to get them by asking for each
// item on its own: a household with sixteen open items fired sixteen
// `GET /api/sent-items/:itemId` requests the moment it landed, each one loading
// scoped sources, awaiting a snapshot and querying the event log.
//
// This asks once. Each entry is byte-identical to what the single-item endpoint
// returns for that id — the server runs the same `getSentItemDetail` — so a row
// rendered from a batch and the same row opened on its own can never disagree.
//
// It lives in its own file rather than in `sentItemsClient.ts` because that file
// is under a contract-parity test that compares its exported type names against
// the API's `lib/sentItems/types.ts`, name for name, both directions. These two
// types belong to a route, not to the shared item contract.

import { api } from '@/lib/api';
import type { SentItemDetailResponse } from '@/lib/sentItemsClient';

/** Why one requested id has no detail. Same vocabulary as the single route. */
export interface SentItemBatchFailure {
  itemId: string;
  error: 'not_found' | 'forbidden' | 'unavailable';
  message: string;
}

export interface SentItemsBatchResponse {
  items: SentItemDetailResponse[];
  /** Never silently dropped: a row that could not load says so on its own row. */
  failures: SentItemBatchFailure[];
  generatedAt: string;
}

/**
 * The server's own cap. Asking for more is a 400, not a truncated success, so
 * the client chunks rather than discovering it.
 */
export const SENT_ITEMS_BATCH_MAX = 40;

/**
 * Every id's detail, in as few requests as the cap allows (one, for any real
 * household). Repeated `ids` params rather than a comma-joined list: an item id
 * is `<kind>:<nativeId>` and carrying it as its own parameter means no
 * separator can ever be ambiguous.
 */
export async function fetchSentItemsBatch(itemIds: string[]): Promise<SentItemsBatchResponse> {
  const ids = [...new Set(itemIds.map((id) => id.trim()).filter(Boolean))];
  if (ids.length === 0) {
    return { items: [], failures: [], generatedAt: new Date().toISOString() };
  }

  const chunks: string[][] = [];
  for (let i = 0; i < ids.length; i += SENT_ITEMS_BATCH_MAX) {
    chunks.push(ids.slice(i, i + SENT_ITEMS_BATCH_MAX));
  }

  const pages = await Promise.all(
    chunks.map((chunk) => {
      const q = chunk.map((id) => `ids=${encodeURIComponent(id)}`).join('&');
      return api.get<SentItemsBatchResponse>(`/api/sent-items/batch?${q}`);
    }),
  );

  return {
    items: pages.flatMap((p) => p.items ?? []),
    failures: pages.flatMap((p) => p.failures ?? []),
    generatedAt: pages[0]?.generatedAt ?? new Date().toISOString(),
  };
}
